import React, { useState, useRef, useEffect } from "react";
import clsx from "clsx";
import { DayPicker } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import { Line } from "react-chartjs-2";
import { toast } from "react-toastify";

import styles from "../../customDashboard.module.css";
import HeaderTitle from "../../../../../components/header/headerTitle/headerTitle";
import Button from "../../../../../components/button/button";
import FilterSelect from "../../../../../components/predictive/filterSelect/filterSelect";
import useOutsideAlerter from "../../../../../utils/clickOutside";
import { formatDate } from "../../../../../utils/formatter";
import { useInstitution } from "../../../../../context/InstitutionContext";
import { 
  readAvailableIndicators, 
  previewIndicator, 
  updateCustomizerDashboard,
  getDashboardData
} from "../../../../../services/customDashboard";
import { insightsFilters } from "../../../../../services/insights";
import { readByInstitution } from "../../../../../services/academetrics";
import { saveDateRange } from "../../../../../utils/localStorage";

// Icons
import DashboardIcon from "../../../../../assets/icons/dashboard-icon.svg";
import BookIcon from "../../../../../assets/icons/book.svg";
import CourseIcon from "../../../../../assets/icons/course.svg";
import LocaleIcon from "../../../../../assets/icons/locale.svg";
import ClassesIcon from "../../../../../assets/icons/classes.svg";
import DisciplineIcon from "../../../../../assets/icons/discipline.svg";
import CalendarIcon from "../../../../../assets/icons/calendar.svg";
import MagnifyingGlassIcon from "../../../../../assets/icons/magnifying-glass-blue.svg";
import ChartMixedPorcentageIcon from "../../../../../assets/icons/chart-mixed-porcentage.svg";
import ChartMixedUpCircleDollarIcon from "../../../../../assets/icons/chart-mixed-up-circle-dollar.svg";
import ChartMixedDownCircleDollarIcon from "../../../../../assets/icons/chart-mixed-down-circle-dollar.svg";
import ChartMoneyIcon from "../../../../../assets/icons/chart-money.svg";
import FinanceIcon from "../../../../../assets/icons/finance.svg";
import AcademicIcon from "../../../../../assets/icons/academic.svg";
import ArrowLeftIcon from "../../../../../assets/icons/arrow-left.svg";
import UserIcon from "../../../../../assets/icons/user.svg";
import UsersIcon from "../../../../../assets/icons/users.svg";
import UsersTwoIcon from "../../../../../assets/icons/users-two.svg";

// Mapeamento de ícones
const ICON_MAP = {
  "chart-mixed-porcentage": ChartMixedPorcentageIcon,
  "chart-mixed-up-circle-dollar": ChartMixedUpCircleDollarIcon,
  "chart-mixed-down-circle-dollar": ChartMixedDownCircleDollarIcon,
  "chart-money": ChartMoneyIcon,
  "finance": FinanceIcon,
  "academic": AcademicIcon,
  "dashboard-icon": DashboardIcon,
  "book": BookIcon,
  "course": CourseIcon,
  "locale": LocaleIcon,
  "classes": ClassesIcon,
  "discipline": DisciplineIcon
};

const getIconByName = (iconName) => {
  return ICON_MAP[iconName] || ChartMixedDownCircleDollarIcon;
};

const INITIAL_BOX_SIZES = {
  box1: { width: 350, height: 280 },
  box2: { width: 350, height: 280 },
  box3: { width: 350, height: 280 },
  box4: { width: 350, height: 280 },
  box5: { width: 350, height: 280 },
  box6: { width: 350, height: 280 },
  box7: { width: 350, height: 280 },
  box8: { width: 350, height: 280 },
  box9: { width: 350, height: 280 }
};

const INITIAL_WIDGETS = {
  box1: null,
  box2: null,
  box3: null,
  box4: null,
  box5: null,
  box6: null,
  box7: null,
  box8: null,
  box9: null
};

const DashboardEditor = ({ 
  dashboardId,
  onCancel, 
  onDashboardUpdated,
  currentUserId,
  dateRange: initialDateRange
}) => {
  const calendarRef = useRef(null);
  const creatorGridRef = useRef(null);
  const { institution } = useInstitution();
  const [maxBoxWidth, setMaxBoxWidth] = useState(800);

  // Estado de carregamento inicial
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [dashboardInfo, setDashboardInfo] = useState(null);

  // Estados do editor
  const [editorWidgets, setEditorWidgets] = useState(INITIAL_WIDGETS);
  const [availableIndicators, setAvailableIndicators] = useState([]);
  const [loadingIndicators, setLoadingIndicators] = useState(true);
  const [boxSizes, setBoxSizes] = useState(INITIAL_BOX_SIZES);
  const [boxLoading, setBoxLoading] = useState({
    box1: false, box2: false, box3: false,
    box4: false, box5: false, box6: false,
    box7: false, box8: false, box9: false
  });
  const [boxPreviewData, setBoxPreviewData] = useState(INITIAL_WIDGETS);
  const [draggingBox, setDraggingBox] = useState(null);

  // Estados de filtros
  const [knowledgeAreas, setKnowledgeAreas] = useState([]);
  const [selectedKnowledgeArea, setSelectedKnowledgeArea] = useState("");
  const [knowledgeAreasLoading, setKnowledgeAreasLoading] = useState(false);
  const [knowledgeAreasFetched, setKnowledgeAreasFetched] = useState(false);

  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [disciplinesLoading, setDisciplinesLoading] = useState(false);
  const [disciplinesFetched, setDisciplinesFetched] = useState(false);

  const [courseTypes, setCourseTypes] = useState([]);
  const [selectedCourseType, setSelectedCourseType] = useState("");
  const [courseTypesLoading, setCourseTypesLoading] = useState(false);
  const [courseTypesFetched, setCourseTypesFetched] = useState(false);

  const [localities, setLocalities] = useState([]);
  const [selectedLocality, setSelectedLocality] = useState("");
  const [localitiesLoading, setLocalitiesLoading] = useState(false);
  const [localitiesFetched, setLocalitiesFetched] = useState(false);

  const [modalities, setModalities] = useState([]);
  const [selectedModality, setSelectedModality] = useState("");
  const [modalitiesLoading, setModalitiesLoading] = useState(false);
  const [modalitiesFetched, setModalitiesFetched] = useState(false);

  // Estados de data
  const [dateRange, setDateRange] = useState(initialDateRange || {
    from: new Date(new Date().getFullYear(), 0, 1),
    to: new Date()
  });
  const [dateRangeString, setDateRangeString] = useState("");
  const [suggestionRange, setSuggestionRange] = useState("");
  const [customMonths, setCustomMonths] = useState("24");
  const [customYears, setCustomYears] = useState("7");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Estados do modal de salvar
  const [savingDashboard, setSavingDashboard] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [dashboardName, setDashboardName] = useState("");
  const [dashboardDescription, setDashboardDescription] = useState("");
  const [visibilityScope, setVisibilityScope] = useState("user");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false));

  // Persistir data no localStorage para compartilhar entre telas
  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      saveDateRange(dateRange);
    }
  }, [dateRange]);

  // Carregar dashboard existente e indicadores ao montar
  useEffect(() => {
    const loadDashboardAndIndicators = async () => {
      try {
        setLoadingDashboard(true);
        setLoadingIndicators(true);

        // Carregar indicadores disponíveis
        const indicators = await readAvailableIndicators();
        setAvailableIndicators(indicators || []);
        setLoadingIndicators(false);

        // Carregar dados do dashboard existente
        if (dashboardId && currentUserId && institution?.id) {
          const currentYear = new Date().getFullYear();
          const startDate = initialDateRange?.from 
            ? initialDateRange.from.toISOString().split('T')[0].replaceAll("-", "/")
            : `${currentYear}/01/01`;
          const endDate = initialDateRange?.to 
            ? initialDateRange.to.toISOString().split('T')[0].replaceAll("-", "/")
            : new Date().toISOString().split('T')[0].replaceAll("-", "/");

          const dashboard = await getDashboardData(
            dashboardId, 
            currentUserId, 
            institution.id,
            startDate,
            endDate
          );

          if (dashboard) {
            setDashboardInfo(dashboard);
            setDashboardName(dashboard.name || "");
            setDashboardDescription(dashboard.description || "");
            setVisibilityScope(dashboard.visibilityScope || "user");

            // Carregar filtros globais se existirem
            if (dashboard.globalFilters) {
              if (dashboard.globalFilters.courseType) {
                setSelectedCourseType(dashboard.globalFilters.courseType);
              }
              if (dashboard.globalFilters.knowledgeArea) {
                setSelectedKnowledgeArea(dashboard.globalFilters.knowledgeArea);
              }
              if (dashboard.globalFilters.course) {
                setSelectedDiscipline(dashboard.globalFilters.course);
              }
              if (dashboard.globalFilters.locality) {
                setSelectedLocality(dashboard.globalFilters.locality);
              }
              if (dashboard.globalFilters.modality) {
                setSelectedModality(dashboard.globalFilters.modality);
              }
            }

            // Mapear widgets existentes para as boxes
            if (dashboard.widgets && dashboard.widgets.length > 0) {
              const newWidgets = { ...INITIAL_WIDGETS };
              const newSizes = { ...INITIAL_BOX_SIZES };
              const newPreviewData = { ...INITIAL_WIDGETS };

              dashboard.widgets.forEach((widget, index) => {
                // Determinar qual box usar baseado na posição ou índice
                const boxIndex = widget.row !== undefined && widget.col !== undefined
                  ? widget.row * 3 + widget.col
                  : index;
                const boxId = `box${boxIndex + 1}`;

                if (boxIndex < 9) {
                  // Encontrar o indicador correspondente
                  const indicator = indicators?.find(ind => ind.code === widget.indicatorCode);
                  
                  newWidgets[boxId] = {
                    code: widget.indicatorCode,
                    name: widget.indicatorName || indicator?.name,
                    description: widget.indicatorDescription || indicator?.description,
                    icon: widget.indicatorIcon || indicator?.icon,
                    category: indicator?.category,
                    visualizationType: widget.visualizationType
                  };

                  // Definir tamanho do widget
                  if (widget.layout?.width || widget.layout?.height) {
                    newSizes[boxId] = {
                      width: (widget.layout?.width || 1) * 350,
                      height: (widget.layout?.height || 1) * 280
                    };
                  }

                  // Definir dados de preview se existirem
                  if (widget.data) {
                    newPreviewData[boxId] = {
                      ...widget,
                      name: widget.indicatorName || indicator?.name,
                      icon: widget.indicatorIcon || indicator?.icon
                    };
                  }
                }
              });

              setEditorWidgets(newWidgets);
              setBoxSizes(newSizes);
              setBoxPreviewData(newPreviewData);
            }
          }
        }
      } catch (error) {
        console.error("Erro ao carregar dashboard:", error);
        toast.error("Erro ao carregar dashboard para edição");
      } finally {
        setLoadingDashboard(false);
      }
    };

    loadDashboardAndIndicators();
  }, [dashboardId, currentUserId, institution]);

  useEffect(() => {
    setDateRangeString(
      `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    );
  }, [dateRange]);

  useEffect(() => {
    const updateMaxWidth = () => {
      if (creatorGridRef.current) {
        const containerWidth = creatorGridRef.current.offsetWidth;
        setMaxBoxWidth(Math.max(280, containerWidth - 30));
      }
    };

    updateMaxWidth();
    window.addEventListener('resize', updateMaxWidth);
    return () => window.removeEventListener('resize', updateMaxWidth);
  }, []);

  // Funções de filtros
  const fetchKnowledgeAreas = async () => {
    if (knowledgeAreas.length > 0) return;
    try {
      setKnowledgeAreasLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "knowledgearea"
      });
      setKnowledgeAreas(response.filterValues || []);
      setKnowledgeAreasFetched(true);
    } catch (error) {
      console.error("Erro ao buscar áreas de conhecimento:", error);
    } finally {
      setKnowledgeAreasLoading(false);
    }
  };

  const fetchDisciplines = async () => {
    if (disciplines.length > 0) return;
    try {
      setDisciplinesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        KnowledgeAreaFilter: selectedKnowledgeArea,
        predictiveFilter: "filterclass"
      });
      setDisciplines(response.filterValues || []);
      setDisciplinesFetched(true);
    } catch (error) {
      console.error("Erro ao buscar cursos:", error);
    } finally {
      setDisciplinesLoading(false);
    }
  };

  const fetchCourseTypes = async () => {
    if (courseTypes.length > 0) return;
    try {
      setCourseTypesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filterdegree"
      });
      setCourseTypes(response.filterValues || []);
      setCourseTypesFetched(true);
    } catch (error) {
      console.error("Erro ao buscar tipos de curso:", error);
    } finally {
      setCourseTypesLoading(false);
    }
  };

  const fetchLocalities = async () => {
    if (localities.length > 0) return;
    try {
      setLocalitiesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filterlocation"
      });
      setLocalities(response.filterValues || []);
      setLocalitiesFetched(true);
    } catch (error) {
      console.error("Erro ao buscar localidades:", error);
    } finally {
      setLocalitiesLoading(false);
    }
  };

  const fetchModalities = async () => {
    if (modalities.length > 0) return;
    try {
      setModalitiesLoading(true);
      const response = await insightsFilters({
        institutionId: institution.id,
        predictiveFilter: "filtermodality"
      });
      setModalities(response.filterValues || []);
      setModalitiesFetched(true);
    } catch (error) {
      console.error("Erro ao buscar modalidades:", error);
    } finally {
      setModalitiesLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (availableUsers.length > 0 || !institution?.id) return;
    
    setLoadingUsers(true);
    try {
      const response = await readByInstitution(institution.id);
      if (response?.users) {
        const filteredUsers = response.users.filter(user => user.id !== currentUserId);
        setAvailableUsers(filteredUsers);
      }
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      toast.error("Erro ao carregar lista de usuários");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleKnowledgeAreaChange = (e) => {
    setSelectedKnowledgeArea(e.target.value);
    setSelectedDiscipline("");
    setDisciplines([]);
    setDisciplinesFetched(false);
  };

  const handleDisciplineChange = (e) => {
    setSelectedDiscipline(e.target.value);
  };

  const handleCourseTypeChange = (e) => {
    setSelectedCourseType(e.target.value);
  };

  const handleLocalityChange = (e) => {
    setSelectedLocality(e.target.value);
  };

  const handleModalityChange = (e) => {
    setSelectedModality(e.target.value);
  };

  const selectSuggestionRange = (suggestionRange) => {
    setSuggestionRange(suggestionRange);
    const currentYear = new Date().getFullYear();

    let selectedRange;
    switch (suggestionRange) {
      case "today":
        selectedRange = { from: new Date(), to: new Date() };
        break;
      case "last_7":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 7)),
          to: new Date()
        };
        break;
      case "last_30": {
        const last_30_now = new Date();
        selectedRange = {
          from: new Date(last_30_now.getFullYear(), last_30_now.getMonth(), 1),
          to: new Date(last_30_now.getFullYear(), last_30_now.getMonth() + 1, 0)
        };
        break;
      }
      case "first_semester":
        selectedRange = {
          from: new Date(`${currentYear}-01-01T00:00:00`),
          to: new Date(`${currentYear}-06-30T23:59:59`)
        };
        break;
      case "second_semester":
        selectedRange = {
          from: new Date(`${currentYear}-07-01T00:00:00`),
          to: new Date(`${currentYear}-12-31T23:59:59`)
        };
        break;
      case "last_semester": {
        const today = new Date();
        const isSecondSemester = today.getMonth() >= 6;
        if (isSecondSemester) {
          selectedRange = {
            from: new Date(`${currentYear}-01-01T00:00:00`),
            to: new Date(`${currentYear}-06-30T23:59:59`)
          };
        } else {
          const lastYear = currentYear - 1;
          selectedRange = {
            from: new Date(`${lastYear}-07-01T00:00:00`),
            to: new Date(`${lastYear}-12-31T23:59:59`)
          };
        }
        break;
      }
      case "last_year": {
        const now = new Date();
        selectedRange = {
          from: new Date(now.getFullYear() - 1, now.getMonth(), 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0)
        };
        break;
      }
      case "last_x_months": {
        const monthsBack = parseInt(customMonths, 10);
        if (!isNaN(monthsBack) && monthsBack > 0) {
          const now_lxm = new Date();
          selectedRange = {
            from: new Date(now_lxm.getFullYear(), now_lxm.getMonth() - monthsBack, now_lxm.getDate()),
            to: new Date()
          };
        } else {
          return;
        }
        break;
      }
      case "current_year":
        selectedRange = {
          from: new Date(currentYear, 0, 1),
          to: new Date()
        };
        break;
      case "previous_year": {
        const previousYear = currentYear - 1;
        selectedRange = {
          from: new Date(`${previousYear}-01-01T00:00:00`),
          to: new Date(`${previousYear}-12-31T23:59:59`)
        };
        break;
      }
      case "last_5_years":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 5)),
          to: new Date()
        };
        break;
      case "last_x_years": {
        const yearsBack = parseInt(customYears, 10);
        if (!isNaN(yearsBack) && yearsBack > 0) {
          const startYear = currentYear - yearsBack;
          selectedRange = {
            from: new Date(`${startYear}-01-01T00:00:00`),
            to: new Date(`${currentYear - 1}-12-31T23:59:59`)
          };
        } else {
          return;
        }
        break;
      }
      default:
        selectedRange = {
          from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
          to: new Date()
        };
        break;
    }

    setDateRange(selectedRange);
    setDateRangeString(
      `${formatDate(selectedRange.from)} - ${formatDate(selectedRange.to)}`
    );
    setIsCalendarOpen(false);
  };

  const selectManualRange = () => {
    setSuggestionRange("");
    setIsCalendarOpen(false);
  };

  const handleClearDateSelection = () => {
    setSuggestionRange("");
    setDateRange({ from: new Date(), to: new Date() });
  };

  // Funções de indicadores
  const handleAddIndicatorToBox = async (boxId, indicator) => {
    setEditorWidgets((prev) => ({
      ...prev,
      [boxId]: indicator
    }));

    setBoxLoading((prev) => ({
      ...prev,
      [boxId]: true
    }));

    try {
      const currentYear = new Date().getFullYear();
      const startDate = dateRange?.from 
        ? dateRange.from.toISOString() 
        : new Date(currentYear, 0, 1).toISOString();
      const endDate = dateRange?.to 
        ? dateRange.to.toISOString() 
        : new Date().toISOString();

      const extraParameters = {};
      
      if (selectedCourseType) extraParameters.courseType = selectedCourseType;
      if (selectedKnowledgeArea) extraParameters.knowledgeArea = selectedKnowledgeArea;
      if (selectedDiscipline) extraParameters.course = selectedDiscipline;
      if (selectedLocality) extraParameters.locality = selectedLocality;
      if (selectedModality) extraParameters.modality = selectedModality;

      const params = {
        indicatorCode: indicator.code,
        visualizationType: indicator.visualizationType,
        institutionId: institution?.id,
        startDate,
        endDate,
        extraParameters
      };

      const previewData = await previewIndicator(params);
      
      setBoxPreviewData((prev) => ({
        ...prev,
        [boxId]: previewData
      }));
    } catch (error) {
      console.error("Erro ao buscar preview:", error);
    } finally {
      setBoxLoading((prev) => ({
        ...prev,
        [boxId]: false
      }));
    }
  };

  const handleRemoveEditorWidget = (boxId) => {
    setEditorWidgets((prev) => ({
      ...prev,
      [boxId]: null
    }));
    setBoxPreviewData((prev) => ({
      ...prev,
      [boxId]: null
    }));
  };

  const handleResize = (boxId, size) => {
    setBoxSizes((prev) => ({
      ...prev,
      [boxId]: { width: size.width, height: size.height }
    }));
  };

  // Funções de drag and drop
  const handleBoxDragStart = (boxId) => {
    setDraggingBox(boxId);
  };

  const handleBoxDragEnd = () => {
    setDraggingBox(null);
  };

  const handleBoxDrop = (targetBoxId) => {
    if (!draggingBox || draggingBox === targetBoxId) return;

    const sourceWidget = editorWidgets[draggingBox];
    const targetWidget = editorWidgets[targetBoxId];
    const sourcePreview = boxPreviewData[draggingBox];
    const targetPreview = boxPreviewData[targetBoxId];
    const sourceSize = boxSizes[draggingBox];
    const targetSize = boxSizes[targetBoxId];

    setEditorWidgets((prev) => ({
      ...prev,
      [draggingBox]: targetWidget,
      [targetBoxId]: sourceWidget
    }));

    setBoxPreviewData((prev) => ({
      ...prev,
      [draggingBox]: targetPreview,
      [targetBoxId]: sourcePreview
    }));

    setBoxSizes((prev) => ({
      ...prev,
      [draggingBox]: targetSize,
      [targetBoxId]: sourceSize
    }));

    setDraggingBox(null);
  };

  // Funções do modal de salvar
  const handleOpenSaveModal = () => {
    setShowSaveModal(true);
  };

  const handleCloseSaveModal = () => {
    setShowSaveModal(false);
  };

  const handleVisibilityScopeChange = (scope) => {
    setVisibilityScope(scope);
    if (scope === 'specific') {
      fetchUsers();
    } else {
      setSelectedUsers([]);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const getBoxPosition = (boxId) => {
    const boxNumber = parseInt(boxId.replace('box', '')) - 1;
    const row = Math.floor(boxNumber / 3);
    const col = boxNumber % 3;
    return { row, col };
  };

  const handleUpdateDashboard = async () => {
    if (!currentUserId || !institution?.id || !dashboardId) {
      toast.error("Erro: Dados insuficientes para atualizar");
      return;
    }

    if (!dashboardName.trim()) {
      toast.error("Por favor, preencha o nome do dashboard");
      return;
    }

    const widgets = Object.entries(editorWidgets)
      .filter(([_, widget]) => widget !== null)
      .map(([boxId, widget]) => {
        const { row, col } = getBoxPosition(boxId);
        const size = boxSizes[boxId];
        return {
          indicatorCode: widget.code,
          visualizationType: widget.visualizationType,
          row,
          col,
          width: Math.round(size.width / 350),
          height: Math.round(size.height / 280)
        };
      });

    if (widgets.length === 0) {
      toast.error("Adicione pelo menos um indicador ao dashboard");
      return;
    }

    setSavingDashboard(true);

    try {
      const currentYear = new Date().getFullYear();
      const startDate = dateRange?.from 
        ? dateRange.from.toISOString() 
        : new Date(currentYear, 0, 1).toISOString();
      const endDate = dateRange?.to 
        ? dateRange.to.toISOString() 
        : new Date().toISOString();

      const globalFilters = { startDate, endDate };

      if (selectedCourseType) globalFilters.courseType = selectedCourseType;
      if (selectedKnowledgeArea) globalFilters.knowledgeArea = selectedKnowledgeArea;
      if (selectedDiscipline) globalFilters.course = selectedDiscipline;
      if (selectedLocality) globalFilters.locality = selectedLocality;
      if (selectedModality) globalFilters.modality = selectedModality;

      const payload = {
        dashboardId,
        name: dashboardName.trim(),
        description: dashboardDescription.trim() || "Dashboard personalizado",
        visibilityScope: visibilityScope === 'specific' ? 'institution' : visibilityScope,
        updatedByUserId: currentUserId,
        globalFilters,
        widgets,
        ...(visibilityScope === 'specific' && selectedUsers.length > 0 && {
          allowedUserIds: selectedUsers
        })
      };

      await updateCustomizerDashboard(payload, institution.id);
      toast.success("Dashboard atualizado com sucesso!");
      setShowSaveModal(false);

      if (onDashboardUpdated) {
        onDashboardUpdated();
      }
    } catch (error) {
      console.error("Erro ao atualizar dashboard:", error);
      toast.error("Erro ao atualizar dashboard. Tente novamente.");
    } finally {
      setSavingDashboard(false);
    }
  };

  const renderEditorBox = (boxId) => {
    const widget = editorWidgets[boxId];
    const size = boxSizes[boxId];
    const isLoading = boxLoading[boxId];
    const previewData = boxPreviewData[boxId];
    const isDragging = draggingBox === boxId;
    const isDropTarget = draggingBox && draggingBox !== boxId;

    if (widget) {
      return (
        <Resizable
          width={size.width}
          height={size.height}
          onResize={(e, { size }) => handleResize(boxId, size)}
          minConstraints={[280, 200]}
          maxConstraints={[maxBoxWidth, 600]}
          handle={<div className={styles.resizeHandle} />}
        >
          <div 
            className={clsx(
              styles.creatorBox, 
              styles.hasIndicator,
              isDragging && styles.isDragging,
              isDropTarget && styles.isDropTarget
            )}
            style={{ width: size.width, height: size.height }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleBoxDrop(boxId);
            }}
          >
            <button
              className={styles.removeCreatorBtn}
              onClick={() => handleRemoveEditorWidget(boxId)}
            >
              ×
            </button>
            <div 
              className={styles.creatorBoxDragArea}
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                handleBoxDragStart(boxId);
              }}
              onDragEnd={(e) => {
                e.stopPropagation();
                handleBoxDragEnd();
              }}
            >
              <div className={styles.dragHandle}>⋮⋮</div>
            </div>
            <div className={styles.creatorBoxContent}>
              {isLoading ? (
                <div className={styles.creatorBoxLoading}>
                  {widget.icon && (
                    <img 
                      src={getIconByName(widget.icon)} 
                      alt={widget.name}
                      className={styles.creatorBoxLoadingIcon}
                    />
                  )}
                  <span className={styles.creatorBoxLoadingName}>{widget.name}</span>
                  <span className={styles.creatorBoxLoadingText}>Carregando dados...</span>
                </div>
              ) : previewData ? (
                <div className={styles.creatorBoxPreview}>
                  {previewData.visualizationType === "label" && (
                    <div className={styles.labelCard}>
                      {previewData.icon && (
                        <img 
                          src={getIconByName(previewData.icon)} 
                          alt={previewData.name}
                          className={styles.labelCardIcon}
                        />
                      )}
                      <div className={styles.labelCardHeader}>
                        <h6 className={styles.labelCardTitle}>{previewData.name}</h6>
                        <h2 className={styles.labelCardValue}>
                          {new Intl.NumberFormat('pt-BR', { 
                            style: 'currency', 
                            currency: 'BRL' 
                          }).format(previewData.data?.value || 0)}
                        </h2>
                      </div>
                    </div>
                  )}
                  {previewData.visualizationType === "time_series" && previewData.data?.points && (
                    <div className={styles.chartWidgetContainer}>
                      <div className={styles.chartWidgetHeader}>
                        {previewData.icon && (
                          <img 
                            src={getIconByName(previewData.icon)} 
                            alt={previewData.name}
                            className={styles.chartWidgetIcon}
                          />
                        )}
                        <h4 className={styles.chartWidgetTitle}>{previewData.name}</h4>
                      </div>
                      <div className={styles.chartContainer}>
                        <Line
                          data={{
                            labels: previewData.data.points.map((point) => point.x),
                            datasets: [
                              {
                                label: previewData.name,
                                data: previewData.data.points.map((point) => point.y),
                                borderColor: "#0962AD",
                                backgroundColor: "rgba(9, 98, 173, 0.1)",
                                fill: true,
                                tension: 0.4,
                                pointRadius: 4,
                                pointBackgroundColor: "#0962AD"
                              }
                            ]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false },
                              tooltip: {
                                callbacks: {
                                  label: (context) => {
                                    return new Intl.NumberFormat('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL'
                                    }).format(context.raw);
                                  }
                                }
                              }
                            },
                            scales: {
                              x: {
                                grid: { display: false },
                                ticks: { font: { size: 10 } }
                              },
                              y: {
                                grid: { color: "rgba(0, 0, 0, 0.05)" },
                                ticks: {
                                  font: { size: 10 },
                                  callback: (value) => {
                                    return new Intl.NumberFormat('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                      notation: 'compact'
                                    }).format(value);
                                  }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.creatorBoxInfo}>
                  {widget.icon && (
                    <img 
                      src={getIconByName(widget.icon)} 
                      alt={widget.name}
                      className={styles.creatorBoxIcon}
                    />
                  )}
                  <span className={styles.creatorBoxCategory}>{widget.category}</span>
                  <span className={styles.creatorBoxName}>{widget.name}</span>
                  <span className={styles.creatorBoxDescription}>{widget.description}</span>
                </div>
              )}
            </div>
          </div>
        </Resizable>
      );
    }

    return (
      <div
        className={clsx(
          styles.creatorBox,
          isDropTarget && styles.isDropTarget
        )}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (draggingBox) {
            handleBoxDrop(boxId);
          } else {
            const indicatorData = e.dataTransfer.getData("indicator");
            if (indicatorData) {
              const indicator = JSON.parse(indicatorData);
              handleAddIndicatorToBox(boxId, indicator);
            }
          }
        }}
      >
        <span className={styles.creatorBoxPlaceholder}>Arraste um indicador aqui</span>
      </div>
    );
  };

  const widgetCount = Object.values(editorWidgets).filter(w => w !== null).length;

  // Loading state
  if (loadingDashboard) {
    return (
      <div className={styles.customDashboardWrapper}>
        <HeaderTitle title="Editar Dashboard" icon={DashboardIcon}>
          <Button
            icon={ArrowLeftIcon}
            text="Voltar"
            size="normal"
            variation="outline"
            onClick={onCancel}
          />
        </HeaderTitle>
        <div className={styles.dashboardContent}>
          <div className={styles.emptyState}>
            <p>Carregando dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.customDashboardWrapper}>
      <HeaderTitle title={`Editar: ${dashboardInfo?.name || 'Dashboard'}`} icon={DashboardIcon}>
        <Button
          icon={ArrowLeftIcon}
          text="Voltar"
          size="normal"
          variation="outline"
          onClick={onCancel}
        />
      </HeaderTitle>
      
      <div className={styles.creatorFiltersWrapper}>
        <div className={styles.creatorFiltersContent}>
          <FilterSelect
            icon={CourseIcon}
            label="Tipo de Curso"
            value={selectedCourseType}
            loading={courseTypesLoading}
            options={courseTypes}
            onFocus={fetchCourseTypes}
            hasFetched={courseTypesFetched}
            onChange={handleCourseTypeChange}
          />
          <FilterSelect
            icon={BookIcon}
            label="Área de Conhecimento"
            value={selectedKnowledgeArea}
            loading={knowledgeAreasLoading}
            options={knowledgeAreas}
            onFocus={fetchKnowledgeAreas}
            hasFetched={knowledgeAreasFetched}
            onChange={handleKnowledgeAreaChange}
          />
          <FilterSelect
            icon={DisciplineIcon}
            label="Curso"
            value={selectedDiscipline}
            loading={disciplinesLoading}
            options={disciplines}
            onFocus={fetchDisciplines}
            hasFetched={disciplinesFetched}
            onChange={handleDisciplineChange}
          />
          <FilterSelect
            icon={LocaleIcon}
            label="Localidade"
            value={selectedLocality}
            loading={localitiesLoading}
            options={localities}
            onFocus={fetchLocalities}
            hasFetched={localitiesFetched}
            onChange={handleLocalityChange}
          />
          <FilterSelect
            icon={ClassesIcon}
            label="Modalidade"
            value={selectedModality}
            loading={modalitiesLoading}
            options={modalities}
            onFocus={fetchModalities}
            hasFetched={modalitiesFetched}
            onChange={handleModalityChange}
          />
        </div>
        
        {(selectedCourseType || selectedKnowledgeArea || selectedDiscipline || selectedLocality || selectedModality) && (
          <div className={styles.activeFiltersInfo}>
            <span className={styles.activeFiltersLabel}>Filtros ativos:</span>
            <div className={styles.activeFiltersList}>
              {selectedCourseType && (
                <span className={styles.activeFilterBadge}>
                  Tipo: {courseTypes.find(ct => ct.value === selectedCourseType)?.label || selectedCourseType}
                </span>
              )}
              {selectedKnowledgeArea && (
                <span className={styles.activeFilterBadge}>
                  Área: {knowledgeAreas.find(ka => ka.value === selectedKnowledgeArea)?.label || selectedKnowledgeArea}
                </span>
              )}
              {selectedDiscipline && (
                <span className={styles.activeFilterBadge}>
                  Curso: {disciplines.find(d => d.value === selectedDiscipline)?.label || selectedDiscipline}
                </span>
              )}
              {selectedLocality && (
                <span className={styles.activeFilterBadge}>
                  Localidade: {localities.find(l => l.value === selectedLocality)?.label || selectedLocality}
                </span>
              )}
              {selectedModality && (
                <span className={styles.activeFilterBadge}>
                  Modalidade: {modalities.find(m => m.value === selectedModality)?.label || selectedModality}
                </span>
              )}
            </div>
          </div>
        )}
        
        <div className={styles.creatorFiltersActions}>
          <div className={styles.filterButtonWrapper}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div ref={calendarRef} className={styles.calendarDropdown}>
                <div className={styles.suggestions}>
                  <ul>
                    <li onClick={() => selectSuggestionRange("today")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "today" })}>Hoje</li>
                    <li onClick={() => selectSuggestionRange("last_7")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "last_7" })}>Últimos 7 dias</li>
                    <li onClick={() => selectSuggestionRange("last_30")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "last_30" })}>Último mês</li>
                    <li onClick={() => selectSuggestionRange("first_semester")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "first_semester" })}>Primeiro Semestre</li>
                    <li onClick={() => selectSuggestionRange("second_semester")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "second_semester" })}>Segundo Semestre</li>
                    <li onClick={() => selectSuggestionRange("last_semester")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "last_semester" })}>Último Semestre</li>
                    <li onClick={() => selectSuggestionRange("last_year")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "last_year" })}>Últimos 12 meses</li>
                    <li className={clsx(styles.suggestionsItem, styles.customMonthsItem, { [styles.active]: suggestionRange === "last_x_months" })}>
                      <span>Últimos</span>
                      <input type="number" min="1" className={styles.customMonthsInput} value={customMonths} placeholder="24" onChange={(e) => setCustomMonths(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && customMonths) selectSuggestionRange("last_x_months"); }} onClick={(e) => e.stopPropagation()} />
                      <span style={{ cursor: !customMonths ? 'default' : 'pointer' }} onClick={() => { if (customMonths) selectSuggestionRange("last_x_months"); }}>meses</span>
                    </li>
                    <li onClick={() => selectSuggestionRange("current_year")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "current_year" })}>Ano Atual</li>
                    <li onClick={() => selectSuggestionRange("previous_year")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "previous_year" })}>Ano Anterior</li>
                    <li onClick={() => selectSuggestionRange("last_5_years")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "last_5_years" })}>Últimos 5 Anos</li>
                    <li className={clsx(styles.suggestionsItem, styles.customMonthsItem, { [styles.active]: suggestionRange === "last_x_years" })}>
                      <span>Últimos</span>
                      <input type="number" min="1" className={styles.customMonthsInput} value={customYears} placeholder="7" onChange={(e) => setCustomYears(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && customYears) selectSuggestionRange("last_x_years"); }} onClick={(e) => e.stopPropagation()} />
                      <span style={{ cursor: !customYears ? 'default' : 'pointer' }} onClick={() => { if (customYears) selectSuggestionRange("last_x_years"); }}>anos</span>
                    </li>
                    <li onClick={() => selectSuggestionRange("manual")} className={clsx(styles.suggestionsItem, { [styles.active]: suggestionRange === "manual" })}>Personalizado</li>
                  </ul>
                  <div>
                    <div onClick={handleClearDateSelection} className={clsx(styles.suggestionsItem, styles.active)}>Limpar seleção</div>
                    <div onClick={() => selectManualRange()} className={clsx(styles.suggestionsItem, styles.active, styles["btn-search-data"])}>
                      <img src={MagnifyingGlassIcon} alt="magnifying-glass" />
                      Buscar dados
                    </div>
                  </div>
                </div>
                <div className={styles.dateRangeGroup}>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Início</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.from}
                      defaultMonth={dateRange?.from}
                      onSelect={(from) => setDateRange((prev) => ({ ...prev, from }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Fim</label>
                    <DayPicker
                      mode="single"
                      selected={dateRange?.to}
                      defaultMonth={dateRange?.to}
                      onSelect={(to) => setDateRange((prev) => ({ ...prev, to }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.creatorContainer}>
        <div className={styles.creatorGridArea}>
          <div ref={creatorGridRef} className={styles.creatorGrid}>
            {renderEditorBox("box1")}
            {renderEditorBox("box2")}
            {renderEditorBox("box3")}
            {renderEditorBox("box4")}
            {renderEditorBox("box5")}
            {renderEditorBox("box6")}
            {renderEditorBox("box7")}
            {renderEditorBox("box8")}
            {renderEditorBox("box9")}
          </div>
        </div>
        <div className={styles.indicatorsSidebar}>
          <h3 className={styles.sidebarTitle}>Indicadores</h3>
          <div className={styles.indicatorsList}>
            {loadingIndicators ? (
              <p className={styles.loadingText}>Carregando indicadores...</p>
            ) : availableIndicators.length === 0 ? (
              <p className={styles.emptyText}>Nenhum indicador disponível</p>
            ) : (
              <>
                {["Financeiro", "Educacional"].map((category) => {
                  const categoryIndicators = availableIndicators.filter(
                    (ind) => ind.category === category
                  );
                  if (categoryIndicators.length === 0) return null;
                  return (
                    <div key={category} className={styles.indicatorCategory}>
                      <h4 className={styles.categoryTitle}>{category}</h4>
                      {categoryIndicators.map((indicator) => (
                        <div
                          key={indicator.indicatorId}
                          className={styles.indicatorItem}
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.setData("indicator", JSON.stringify(indicator));
                          }}
                        >
                          {indicator.icon && (
                            <img 
                              src={getIconByName(indicator.icon)} 
                              alt={indicator.name}
                              className={styles.indicatorIcon}
                            />
                          )}
                          <div className={styles.indicatorInfo}>
                            <span className={styles.indicatorName}>{indicator.name}</span>
                            <span className={styles.indicatorDescription}>{indicator.description}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.createButtonWrapper}>
        <div className={styles.footerInfo}>
          <span className={styles.footerText}>
            {widgetCount === 0 
              ? "Arraste indicadores para os espaços vazios para montar seu dashboard"
              : `${widgetCount} ${widgetCount === 1 ? 'indicador' : 'indicadores'} neste dashboard.`
            }
          </span>
        </div>
        <div className={styles.footerActions}>
          <Button
            text="Cancelar"
            size="normal"
            variation="outline"
            onClick={onCancel}
          />
          <Button
            text="Salvar Alterações"
            size="normal"
            variation="primary"
            onClick={handleOpenSaveModal}
            disabled={widgetCount === 0}
          />
        </div>
      </div>

      {showSaveModal && (
        <div className={styles.modalBackdrop} onClick={handleCloseSaveModal}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeaderNew}>
              <h3>Salvar Alterações</h3>
              <button className={styles.closeButtonNew} onClick={handleCloseSaveModal}>
                ×
              </button>
            </div>

            <p className={styles.modalDescription}>
              Atualize as informações do seu dashboard.
            </p>

            <div className={styles.formGroup}>
              <label htmlFor="dashboard-name">Nome do Dashboard *</label>
              <input
                id="dashboard-name"
                type="text"
                className={styles.input}
                placeholder="Ex: Meu Dashboard Financeiro"
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                maxLength={100}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="dashboard-description">Descrição</label>
              <textarea
                id="dashboard-description"
                className={styles.textarea}
                placeholder="Descreva seu dashboard..."
                value={dashboardDescription}
                onChange={(e) => setDashboardDescription(e.target.value)}
                rows={3}
                maxLength={500}
              />
            </div>

            <div className={styles.formGroup}>
              <label>Visibilidade</label>
              <div className={styles.visibilityCards}>
                <div
                  className={`${styles.visibilityCard} ${visibilityScope === 'user' ? styles.visibilityCardActive : ''}`}
                  onClick={() => handleVisibilityScopeChange('user')}
                >
                  <div className={styles.visibilityCardIcon}>
                    <img src={UserIcon} alt="Apenas eu" />
                  </div>
                  <div className={styles.visibilityCardContent}>
                    <span className={styles.visibilityCardLabel}>Apenas eu</span>
                    <span className={styles.visibilityCardDescription}>Visível somente para você</span>
                  </div>
                </div>
                <div
                  className={`${styles.visibilityCard} ${visibilityScope === 'institution' ? styles.visibilityCardActive : ''}`}
                  onClick={() => handleVisibilityScopeChange('institution')}
                >
                  <div className={styles.visibilityCardIcon}>
                    <img src={UsersIcon} alt="Toda instituição" />
                  </div>
                  <div className={styles.visibilityCardContent}>
                    <span className={styles.visibilityCardLabel}>Toda instituição</span>
                    <span className={styles.visibilityCardDescription}>Visível para todos</span>
                  </div>
                </div>
                <div
                  className={`${styles.visibilityCard} ${visibilityScope === 'specific' ? styles.visibilityCardActive : ''}`}
                  onClick={() => handleVisibilityScopeChange('specific')}
                >
                  <div className={styles.visibilityCardIcon}>
                    <img src={UsersTwoIcon} alt="Usuários específicos" />
                  </div>
                  <div className={styles.visibilityCardContent}>
                    <span className={styles.visibilityCardLabel}>Usuários específicos</span>
                    <span className={styles.visibilityCardDescription}>Escolha quem pode ver</span>
                  </div>
                </div>
              </div>

              {visibilityScope === 'specific' && (
                <div className={styles.usersList}>
                  <div className={styles.usersListHeader}>
                    <span className={styles.usersListTitle}>Selecione os usuários</span>
                    {selectedUsers.length > 0 && (
                      <span className={styles.usersListCount}>
                        {selectedUsers.length} selecionado{selectedUsers.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {loadingUsers ? (
                    <div className={styles.usersListLoading}>Carregando usuários...</div>
                  ) : availableUsers.length === 0 ? (
                    <div className={styles.usersListEmpty}>Nenhum usuário disponível</div>
                  ) : (
                    <div className={styles.usersListItems}>
                      {(() => {
                        const roleLabels = {
                          'Admin': 'Administradores',
                          'Manager': 'Gerentes',
                          'Financial': 'Financeiro',
                          'Operator': 'Operadores'
                        };

                        const groupedUsers = availableUsers.reduce((groups, user) => {
                          const role = user.role || 'User';
                          if (!groups[role]) groups[role] = [];
                          groups[role].push(user);
                          return groups;
                        }, {});

                        const roleOrder = ['Admin', 'Manager', 'Financial', 'User', 'Viewer'];
                        const sortedRoles = Object.keys(groupedUsers).sort((a, b) => {
                          const indexA = roleOrder.indexOf(a);
                          const indexB = roleOrder.indexOf(b);
                          if (indexA === -1 && indexB === -1) return a.localeCompare(b);
                          if (indexA === -1) return 1;
                          if (indexB === -1) return -1;
                          return indexA - indexB;
                        });

                        return sortedRoles.map(role => (
                          <div key={role} className={styles.roleGroup}>
                            <div className={styles.roleHeader}>
                              <span className={styles.roleLabel}>{roleLabels[role] || role}</span>
                              <span className={styles.roleCount}>{groupedUsers[role].length}</span>
                            </div>
                            {groupedUsers[role].map(user => (
                              <label 
                                key={user.id} 
                                className={`${styles.userItem} ${selectedUsers.includes(user.id) ? styles.userItemSelected : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(user.id)}
                                  onChange={() => handleUserToggle(user.id)}
                                  className={styles.userCheckbox}
                                />
                                <span className={styles.userName}>{user.firstName} {user.lastName}</span>
                                <span className={styles.userEmailSeparator}>•</span>
                                <span className={styles.userEmail}>{user.email}</span>
                              </label>
                            ))}
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={styles.modalActionsNew}>
              <Button
                text="Cancelar"
                variation="secondary"
                size="medium"
                onClick={handleCloseSaveModal}
                styling={{ padding: "8px 20px", fontSize: "13px" }}
              />
              <Button
                text={savingDashboard ? "Salvando..." : "Salvar"}
                variation="primary"
                size="medium"
                onClick={handleUpdateDashboard}
                disabled={savingDashboard || !dashboardName.trim()}
                styling={{ padding: "8px 20px", fontSize: "13px" }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardEditor;

